import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import {
  addPatientAbhaAddress,
  allRoles,
  allSpecializations,
  createUser,
  EditUser,
  fetchDoctorBookingProfile,
  fetchUserDetails,
  findPatientByAbhaNumber,
  syncPatientWithAbha,
  updateDoctorBookingProfile,
  uploadPatientDocument
} from 'utils/apiHelper';
import ExistingAbhaPatientDialog from 'components/abha/ExistingAbhaPatientDialog';
import {
  PHR_MOBILE_UPDATE_MESSAGE,
  abhaMobileUpdatePending,
  extractAbhaErrorMessage,
  normalizeAbhaNumber,
  tenDigits
} from 'utils/abhaUiUtils';
import AbhaUpdateMobile from 'components/abha/AbhaUpdateMobile';
import PincodeSelector from 'components/Pincode';


import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

// third-party
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
// import 'dayjs/locale/en-gb';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb'

// project imports
import AlertCustomerDelete from './AlertCustomerDelete';
import Avatar from 'components/@extended/Avatar';
import AbhaVerification from 'sections/apps/customer/AbhaVerification';
import PatientStepper from 'sections/apps/customer/PatientStepper';
import IconButton from 'components/@extended/IconButton';
import CircularWithPath from 'components/@extended/progress/CircularWithPath';

import { Gender } from 'config';
import { openSnackbar } from 'api/snackbar';
import useAuth from 'hooks/useAuth';
import { getImageUrl, ImagePath } from 'utils/getImageUrl';
import { isProduction } from 'utils/helper';
import { isAadhaarValid } from 'utils/aadhaarUtils';

// assets
// import CameraOutlined from '@ant-design/icons/CameraOutlined';
// import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DeleteFilled from '@ant-design/icons/DeleteFilled';
import PatientSearchField from 'sections/components-overview/PatientSearchField';
import DoctorBookingFields from './DoctorBookingFields';
import { Autocomplete } from '@mui/material';
import { MaritalStatus } from 'config';

const ERROR_NO_RELIGION_SELECTED = 'Religion is required';

const rangesOverlap = (ranges, groupField) => {
  const groupedRanges = ranges.reduce((groups, range) => {
    if (!range[groupField] && range[groupField] !== 0) return groups;
    const group = String(range[groupField]);
    groups[group] = [...(groups[group] || []), range];
    return groups;
  }, {});

  return Object.values(groupedRanges).some((group) => {
    const openRanges = group
      .filter((range) => !range.is_closed && range.start_time && range.end_time)
      .sort((first, second) => first.start_time.localeCompare(second.start_time));
    const hasClosedConflict = group.some((range) => range.is_closed) && group.length > 1;
    return hasClosedConflict || openRanges.some((range, index) => (
      index > 0 && range.start_time < openRanges[index - 1].end_time
    ));
  });
};

const SPECIAL_PANEL_TYPES = ['SD', 'SK', 'UJVNL', 'STAFF'];

// Fields rendered on the "Doctor Profile" tab of the doctor edit dialog.
const DOCTOR_PROFILE_TAB_FIELDS = ['speciality', 'qualification', 'doctorBooking'];

// Tab holding the first errored field: user details come first, so they win.
const getTabWithFirstError = (errors) => {
  const errorFields = Object.keys(errors || {});
  return errorFields.length && errorFields.every((field) => DOCTOR_PROFILE_TAB_FIELDS.includes(field)) ? 1 : 0;
};

const roleGroups = allRoles.reduce((groups, role) => {
  const existingGroup = groups.find((group) => group.category === role.category);
  if (existingGroup) {
    existingGroup.roles.push(role);
  } else {
    groups.push({ category: role.category, roles: [role] });
  }
  return groups;
}, []);

const CAREOF_SUFFIXES = [
  { value: 'C/O', label: 'C/O (Care of)' },
  { value: 'F/O', label: 'F/O (Father of)' },
  { value: 'S/O', label: 'S/O (Son of)' },
  { value: 'D/O', label: 'D/O (Daughter of)' },
  { value: 'H/O', label: 'H/O (Husband of)' },
  { value: 'W/O', label: 'W/O (Wife of)' }
];

const EMPTY_DOCTOR_BOOKING_PROFILE = {
  practice_start_year: '',
  years_of_experience: null,
  profile_photo: '',
  consultation_fee: '',
  old_patient_fee: '',
  follow_up_fee: '',
  online_booking_enabled: false,
  slot_duration_minutes: 15,
  max_online_bookings_per_day: 20,
  weekly_availability: [],
  date_overrides: [],
  recommended_slot_duration: null
};

// for setting random avatar based on gender
const getRandomAvatar = (gender) => {
  if (gender === Gender.MALE) {
    return Math.floor(Math.random() * 5) + 1; 
  } else {
    return Math.floor(Math.random() * 5) + 5;
  }
};

// constant
// "Maharashtra" (ABHA/Aadhaar) -> "MAHARASHTRA", as the pincode list names states.
const toPincodeState = (name) => String(name || '').trim().toUpperCase();

const getInitialValues = (customer) => {
  const newCustomer = {
    fullName: customer?.full_name || '',
    contact: customer?.phone_number || '',
    avatar: getRandomAvatar(customer?.gender || Gender.MALE),
    gender: customer?.gender || Gender.MALE,
    countryCode: '+91',
    dob: customer?.dob ? dayjs(customer.dob, "YYYY-MM-DD", true) : null,
    email: customer?.email || '',
    aadhar: customer?.aadhar_card || '',
    abhaAddress: customer?.abha_address || '',
    abhaNumber: customer?.abha_number ? String(customer.abha_number) : '',
    pincode: customer?.home_pincode || '',
    village: customer?.village || '',
    state: customer?.metadata?.state || '',
    roles: customer?.roles || ['patient'],
    tribe: customer?.tribe?.split("-")[0] || '',
    tribeName: customer?.tribe?.split("-")[1] || '',
    speciality: customer?.doctor_speciality || '',
    qualification: customer?.doctor_qualification || '',
    password: '',
    confirmPassword: '',
    maritalStatus: customer?.metadata?.marital_status,
    careOf: customer?.careof || '',
    careOfName: customer?.careof_name || customer?.metadata?.careof_name || '',
    careOfSuffix: customer?.metadata?.careof_suffix || 'C/O',
    bloodGroup: customer?.metadata?.blood_group || '',
    address: customer?.address || customer?.metadata?.address || '',
    doctorBooking: { ...EMPTY_DOCTOR_BOOKING_PROFILE },
  };

  if (customer) {
    return _.merge({}, newCustomer, customer, {
      tribe: customer?.tribe?.split("-")[0] || '',
    });
  }

  return newCustomer;
};

// ==============================|| CUSTOMER ADD / EDIT - FORM ||============================== //

export default function FormCustomerAdd({ customer: customerProp, closeModal, type = 'patient', mode: modeProp, handleFormSubmit, skipRoleSelection = false, skipPasswordFields = false, hideSpeciality = false, abhaSession: abhaSessionProp }) {
  // M1 iteration 2 obs 8: "Go with HIMS" on a new registration switches the
  // form to edit the patient the ABHA already belongs to.
  const [switchedPatient, setSwitchedPatient] = useState(null);
  const customer = switchedPatient || customerProp;
  const mode = switchedPatient ? 'edit' : modeProp;
  // The ABHA verification session behind the details in the form: a mobile
  // number changed here must also be verified onto the ABHA (OTP).
  const [abhaSession, setAbhaSession] = useState(() =>
    abhaSessionProp ? { ...abhaSessionProp, baselineContact: tenDigits(customerProp?.phone_number) } : null
  );
  const startAbhaSession = (session, baselineContact) =>
    setAbhaSession(session?.xToken ? { ...session, baselineContact: tenDigits(baselineContact) } : null);
  const [loading, setLoading] = useState(true);
  const [selectedImage] = useState(undefined); // or remove this line entirely
  const [selectedType, setSelectedType] = useState(type);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAbhaVerified, setIsAbhaVerified] = useState(false);
  const [openAbhaModal, setOpenAbhaModal] = useState(false);
  const [openStepperModal, setOpenStepperModal] = useState(false);
  const [abhaPhotoData, setAbhaPhotoData] = useState(null);
  const [doctorProfilePhoto, setDoctorProfilePhoto] = useState(null);
  const [editTab, setEditTab] = useState(0);
  // idle | loading | loaded | failed — booking settings are only saved over a profile that loaded.
  const [bookingProfileStatus, setBookingProfileStatus] = useState('idle');
  const handledSubmitCountRef = useRef(0);
  const submitButtonRef = useRef(null);
  const { roles: currentUserRoles = [], user: currentUser } = useAuth();
  // Mirrors the backend DoctorBookingProfileView: superusers and IT technicians only.
  const canManageDoctorBooking = currentUser?.is_superuser === true
    || currentUserRoles.includes('superuser')
    || currentUserRoles.includes('it_technician');
  const isExistingDoctor = Boolean(customer?.id && customer?.roles?.includes('doctor'));
  const showDoctorProfileTab = isExistingDoctor && canManageDoctorBooking;

  const [avatar, setAvatar] = useState(
    getImageUrl(`avatar-${customer && customer !== null && customer?.avatar ? customer.avatar : 1}.png`, ImagePath.USERS)
  );

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear());


    const handleAbhaProfileVerified = async (profile, _response, choice) => {
    if (!profile) return;

    // M1 iteration 2 obs 8: the ABHA belongs to a patient already in HMS and
    // the user picked on the comparison screen.
    const existingPatient = choice?.existingPatient;
    if (existingPatient && existingPatient.id !== customer?.id) {
      // New registration, but the ABHA is already a patient's: "Go with ABDM"
      // updates that patient; "Go with HIMS" opens it for editing.
      if (choice.useAbhaDetails) await linkAbhaToExistingPatient(profile, existingPatient, true);
      else await openExistingPatientForEdit(profile, existingPatient, choice.abhaSession);
      return;
    }
    if (existingPatient) {
      startAbhaSession(choice.abhaSession, choice.useAbhaDetails ? profile.mobile : formik.values.contact);
    }
    if (existingPatient && choice.useAbhaDetails === false) {
      // "Go with HIMS" while editing: keep the form's details, link the ABHA only.
      if (profile.abhaAddress) formik.setFieldValue('abhaAddress', profile.abhaAddress);
      if (profile.abhaNumber) formik.setFieldValue('abhaNumber', profile.abhaNumber);
      setIsAbhaVerified(true);
      return;
    }
    
    const fullName = [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(' ') || profile.fullName || '';
    if (fullName) formik.setFieldValue('fullName', fullName);
    
    const mobile = profile.mobile || profile.contact;
    if (mobile) formik.setFieldValue('contact', mobile);
    
    if (profile.gender) {
      let g = '';
      const pGender = profile.gender.toLowerCase();
      if (pGender === 'm' || pGender === 'male') g = 'Male';
      else if (pGender === 'f' || pGender === 'female') g = 'Female';
      else if (pGender === 'o' || pGender === 'other') g = 'Other';
      if (g) formik.setFieldValue('gender', g);
    }
    
    if (profile.dateOfBirth) {
       formik.setFieldValue('dob', profile.dateOfBirth);
    } else if (profile.dob) {
       formik.setFieldValue('dob', dayjs(profile.dob, 'YYYY-MM-DD'));
    } else if (profile.yearOfBirth) {
       const yyyy = profile.yearOfBirth;
       const mm = profile.monthOfBirth || '01';
       const dd = profile.dayOfBirth || '01';
       formik.setFieldValue('dob', dayjs(`${yyyy}-${mm}-${dd}`, 'YYYY-MM-DD'));
    }

    if (profile.abhaAddress) formik.setFieldValue('abhaAddress', profile.abhaAddress);
    if (profile.abhaNumber) formik.setFieldValue('abhaNumber', profile.abhaNumber);

    if (profile.address) formik.setFieldValue('address', profile.address);
    if (profile.pincode) formik.setFieldValue('pincode', profile.pincode);
    if (profile.village) formik.setFieldValue('village', profile.village);
    if (profile.state) formik.setFieldValue('state', toPincodeState(profile.state));
    
    if (profile.photo || profile.profilePhoto) {
      const base64Data = profile.photo || profile.profilePhoto;
      const base64String = base64Data.startsWith('data:') ? base64Data : `data:image/jpeg;base64,${base64Data}`;
      setAbhaPhotoData(base64String);
      setAvatar(base64String);
    }
    
    setIsAbhaVerified(true);
  };

  useEffect(() => {
    if (selectedImage) {
      setAvatar(URL.createObjectURL(selectedImage));
    }
  }, [selectedImage]);



  useEffect(() => {
    setLoading(false);
  }, []);

  const handleDateAccept = () => {
    if (submitButtonRef.current) {
      setTimeout(() => {
        submitButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  };


  const CustomerSchema = Yup.object().shape({
    fullName: Yup.string().max(255).required('Full Name is required'),

    // Panel is optional (ABDM M1); a sub-value is only needed once a panel is picked.
    tribe: Yup.string().nullable(),

    tribeName: Yup.string().when('tribe', {
      is: (tribe) => Boolean(tribe),
      then: (schema) => schema.required('Tribe Name is required'),
      otherwise: (schema) => schema.nullable()
    }),

    abhaAddress: Yup.string()
      .trim()
      .test('abha-address-shape', 'ABHA address looks like username@provider (e.g. patient@sbx)', (value) => {
        if (!value) return true;
        return /^[a-z0-9._-]{3,}@[a-z0-9]+$/i.test(value);
      }),

    abhaNumber: Yup.string()
      .transform((value) => (value ? String(value).replace(/\D+/g, '') : value))
      .test('abha-number-length', 'ABHA number must be 14 digits', (value) => {
        if (!value) return true;
        return /^\d{14}$/.test(value);
      }),

    aadhar: Yup.string()
      .matches(/^\d{12}$/, 'Aadhar number must be exactly 12 digits with no spaces')
      .transform(value => value ? value.replace(/\s+/g, '') : value)
      .test('empty-allow', '', value => !value || /^\d{12}$/.test(value)) // Allow empty input while typing
      .test({
        name: 'verhoeff',
        message: 'Invalid Aadhar number',
        test: function (value) {
          return isAadhaarValid(value, { skipChecksum: !isProduction });
        }
      })
      .test('conditional-required', 'Aadhar number is required', function (value) {
        if (selectedType === 'employee' && !value) {
          return false;
        }
        return true;
      }),

    contact: Yup.string()
      .matches(/^\d{10}$/, 'Contact should be exactly 10 digits')
      .test('conditional-required', 'Phone number is required', function (value) {
        if (selectedType === 'employee' && !value) {
          return false;
        }
        return true;
      }),

    email: Yup.string()
      .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Invalid email format')
      .test('conditional-required', 'Email is required', function (value) {
        if (selectedType === 'employee' && !value) {
          return false;
        }
        return true;
      }),

    pincode: Yup.string()
      .matches(/^\d{6}$/, 'Pincode should be exactly 6 digits')
      .required('Pincode is required'),

    address: Yup.string().nullable(),

    // ABDM M1: a patient's state (prefilled from ABHA/Aadhaar) is mandatory.
    state: Yup.string().test('state-required', 'State is required', (value) =>
      selectedType === 'employee' || Boolean(value)
    ),

    dob: Yup.date().required('Date of Birth is required'),

    doctorBooking: Yup.object().shape({
      weekly_availability: Yup.array().test(
        'weekly-ranges-do-not-overlap',
        'Availability times cannot overlap on the same day.',
        (ranges = []) => !rangesOverlap(ranges, 'weekday')
      ),
      date_overrides: Yup.array()
        .of(Yup.object().shape({
          date: Yup.string().test(
            'date-is-not-in-the-past',
            'Date exceptions cannot be in the past.',
            (value) => !value || !dayjs(value).isBefore(dayjs(), 'day')
          )
        }))
        .test(
          'date-ranges-do-not-overlap',
          'Exceptions cannot overlap or mix closed and open hours on the same date.',
          (ranges = []) => !rangesOverlap(ranges.filter((range) => range.date), 'date')
        )
    }),

    ...(selectedType === 'employee'
      ? {
        password: customer
          ? Yup.string().test(
            'minimum-password-length',
            'Password must be at least 6 characters',
            (value) => !value || value.length >= 6
          )
          : Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('Password is required'),

        confirmPassword: customer
          ? Yup.string()
            .test('confirm-new-password', 'Confirm the new password', function (value) {
              return !this.parent.password || Boolean(value);
            })
            .test('matching-passwords', 'Passwords must match', function (value) {
              return !this.parent.password || value === this.parent.password;
            })
          : Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('Confirm Password is required')
            .oneOf([Yup.ref('password'), null], 'Passwords must match'),

        ...(selectedRoles.includes('doctor') ? {
          speciality: Yup.string()
            .required('Speciality is required')
        } : {})
      }
      : {}),
  });

  const [openAlert, setOpenAlert] = useState(false);

  const handleAlertClose = () => {
    setOpenAlert(!openAlert);
    closeModal();
  };

  // extended the panel usage by adding more options
  const [selectedPanel, setSelectedPanel] = useState(customer?.tribe?.split("-")[0] || '');
  const [submitErrors, setSubmitErrors] = useState({});
  // Set when a new registration's ABHA number already belongs to a patient.
  const [existingAbhaMatch, setExistingAbhaMatch] = useState(null);
  const [isLinkingExistingPatient, setIsLinkingExistingPatient] = useState(false);

  const handlePanelChange = (event) => {
    setSelectedPanel(event.target.value);
  };

  const formik = useFormik({
    initialValues: getInitialValues(customer),
    validationSchema: CustomerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitErrors({});
      const errors = await formik.validateForm();

      // Prevent submission if errors exist
      if (Object.keys(errors).length > 0) {
        setSubmitErrors(errors);
        setEditTab(getTabWithFirstError(errors));
        setSubmitting(false);
        return;
      }


      const modifiedData = {
        full_name: values.fullName,
        email: values.email,
        gender: values.gender,
        roles: (values.roles?.includes("patient") ? values.roles : ["patient", ...values.roles]),
        tribe: values.tribe ? (values.tribe + "-" + (values.tribeName || '')) : null,
        aadhar_card: (values.aadhar === '' ? null : values.aadhar),
        abha_address: (values.abhaAddress?.trim() ? values.abhaAddress.trim() : null),
        abha_number: (values.abhaNumber ? String(values.abhaNumber).replace(/\D+/g, '') || null : null),
        dob: values?.dob ? dayjs(values.dob).format("YYYY-MM-DD") : null,
        careof: values.careOf,
        avatar: values.avatar,
        phone_number: values.contact === '' ? null : values.contact,
        pin_code: values.pincode === '' ? null : values.pincode,
        village: values.village || null,
        religion: values.religion,
        doctor_speciality: (values.roles.includes("doctor") ? values.speciality : ""),
        doctor_qualification: ((values.roles.includes("doctor") || values.roles.includes("superuser")) ? values.qualification : ""),
        home_pincode: values.pincode === '' ? null : values.pincode,
        address: values.address || null,
        metadata: {
          address: values.address || null,
          marital_status: values.maritalStatus,
          careof_name: values.careOfName,
          careof_suffix: values.careOfSuffix,
          blood_group: values.bloodGroup,
          state: values.state || null
        },
      }
      if (selectedType === "employee" && (!customer || values.password)) {
        modifiedData.password = values.password;
      }
      try {
        let savedUserId = customer?.id;
        if (abhaMobileUpdatePending(abhaSession, values.contact)) {
          openSnackbar({
            open: true,
            message: abhaSession.isPhrLogin
              ? PHR_MOBILE_UPDATE_MESSAGE
              : 'Verify the new mobile number on the ABHA (OTP) before saving.',
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
            variant: 'alert',
            alert: { color: 'warning' }
          });
          return;
        }
        if (mode === "edit") {
          if (customer?.id) {
            modifiedData.id = customer.id;
            await EditUser(modifiedData);
            if (localStorage.getItem('comingFromAppointmentForm') === 'true') {
              localStorage.setItem(
                'newlyRegisteredPatient',
                JSON.stringify({ id: customer.id, full_name: values.fullName, phone_number: values.contact || '' })
              );
              localStorage.removeItem('comingFromAppointmentForm');
            }
          } else {
            console.log("User id not found");
          }
        }
        else {
          // One ABHA number = one patient/UHID. If it is already registered,
          // offer to continue with that patient (adding the new ABHA address)
          // instead of failing with "user with this abha number already exists".
          if (selectedType === 'patient' && modifiedData.abha_number) {
            try {
              if (await openExistingAbhaPatient(modifiedData)) return;
            } catch (lookupError) {
              // Fall through to create; if the number is taken, the catch
              // below retries the lookup and reports why it failed.
              console.error('ABHA number lookup failed:', lookupError);
            }
          }
          const response = await createUser(modifiedData);
          savedUserId = response?.user?.id || response?.data?.id || response?.id;
          const newPatientId = response?.data?.id || response?.user?.id || response?.id;
          
          if (newPatientId && abhaPhotoData) {
            try {
              const res = await fetch(abhaPhotoData);
              const blob = await res.blob();
              const file = new File([blob], "patient_profile_image.jpg", { type: "image/jpeg" });
              await uploadPatientDocument(newPatientId, file, 'other', 'Patient Profile Image (ABHA)');
            } catch(e) {
               console.error("Failed to upload ABHA photo", e);
            }
          }
          
          if (localStorage.getItem('comingFromAppointmentForm') === 'true') {
            const patientData = {
              id: response?.data?.id || response?.user?.id,
              full_name: values.fullName,
              phone_number: values.contact || '',
            };
            localStorage.setItem('newlyRegisteredPatient', JSON.stringify(patientData));
            localStorage.removeItem('comingFromAppointmentForm');
          }
        }

        // Never save the blank defaults over an existing doctor's profile that failed to load.
        const canSaveBookingProfile = canManageDoctorBooking
          && (!isExistingDoctor || bookingProfileStatus === 'loaded');
        if (values.roles.includes('doctor') && savedUserId && canSaveBookingProfile) {
          await updateDoctorBookingProfile(savedUserId, values.doctorBooking, doctorProfilePhoto);
        }
        
        openSnackbar({
          open: true,
          message: 'User successfully added!',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          variant: 'alert',
          alert: { color: 'success' },
        });
        
        handleFormSubmit();
        closeModal();
      } catch (error) {
        let errorMessage = 'Failed to add user. Please try again.';
        // Duplicate ABHA number on create: show the existing patient instead.
        if (mode !== 'edit' && selectedType === 'patient' && modifiedData.abha_number && error?.response?.data?.abha_number) {
          try {
            if (await openExistingAbhaPatient(modifiedData)) {
              setSubmitErrors({});
              return;
            }
          } catch (lookupError) {
            setSubmitErrors(error.response.data);
            openSnackbar({
              open: true,
              message: `This ABHA number is already registered, but the existing patient could not be loaded: ${extractAbhaErrorMessage(lookupError, 'lookup failed')}`,
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
              variant: 'alert',
              alert: { color: 'error' }
            });
            return;
          }
        }
        setSubmitErrors(error?.response?.data);
        
        if (error.response?.data) {
          // Get the first error message from the response
          const firstError = Object.values(error.response.data)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
        
        openSnackbar({
          open: true,
          message: errorMessage,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          variant: 'alert',
          alert: { color: 'error' },
        });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, setFieldValue } = formik;

  useEffect(() => {
    if (!isExistingDoctor || !canManageDoctorBooking) return undefined;

    let active = true;
    setBookingProfileStatus('loading');
    fetchDoctorBookingProfile(customer.id)
      .then((profile) => {
        if (!active) return;
        setBookingProfileStatus('loaded');
        setFieldValue('doctorBooking', {
          ...EMPTY_DOCTOR_BOOKING_PROFILE,
          ...profile,
          consultation_fee: profile.consultation_fee ?? '',
          old_patient_fee: profile.old_patient_fee ?? '',
          follow_up_fee: profile.follow_up_fee ?? '',
          // The saved duration wins; the recommendation is only a fallback and stays visible as a hint.
          slot_duration_minutes:
            profile.slot_duration_minutes ??
            profile.recommended_slot_duration?.minutes ??
            EMPTY_DOCTOR_BOOKING_PROFILE.slot_duration_minutes,
          // The pickers work in "HH:MM"; the API returns "HH:MM:SS".
          weekly_availability: (profile.weekly_availability || []).map((range) => ({
            ...range,
            start_time: range.start_time?.slice(0, 5) || '',
            end_time: range.end_time?.slice(0, 5) || ''
          })),
          date_overrides: (profile.date_overrides || []).map((override) => ({
            ...override,
            start_time: override.start_time?.slice(0, 5) || '',
            end_time: override.end_time?.slice(0, 5) || ''
          }))
        });
      })
      .catch(() => {
        if (!active) return;
        setBookingProfileStatus('failed');
        openSnackbar({
          open: true,
          message: 'Unable to load doctor booking settings.',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          variant: 'alert',
          alert: { color: 'error' }
        });
      });

    return () => {
      active = false;
    };
  }, [customer?.id, isExistingDoctor, canManageDoctorBooking, setFieldValue]);

  // Formik skips onSubmit when validation fails, so jump to the errored tab after each attempt.
  useEffect(() => {
    if (formik.isSubmitting || formik.submitCount === handledSubmitCountRef.current) return;
    handledSubmitCountRef.current = formik.submitCount;
    if (showDoctorProfileTab && Object.keys(formik.errors).length) {
      setEditTab(getTabWithFirstError(formik.errors));
    }
  }, [formik.isSubmitting, formik.submitCount, formik.errors, showDoctorProfileTab]);

  useEffect(() => {
    if (customer) {
      if (customer.abha_number) {
        setIsAbhaVerified(true);
      }
      return;
    }

    const storedAbhaProfile = localStorage.getItem('abhaVerifiedProfile');
    if (!storedAbhaProfile) return;

    try {
      const profile = JSON.parse(storedAbhaProfile);
      formik.setValues((prev) => ({
        ...prev,
        fullName: profile?.fullName || prev.fullName,
        contact: profile?.contact || prev.contact,
        gender: profile?.gender || prev.gender,
        dob: profile?.dob ? dayjs(profile.dob, 'YYYY-MM-DD', true) : prev.dob,
        email: profile?.email || prev.email,
        pincode: profile?.pincode || prev.pincode,
        address: profile?.address || prev.address,
        village: profile?.village || prev.village,
        state: profile?.state ? toPincodeState(profile.state) : prev.state,
        abhaNumber: profile?.abhaNumber || prev.abhaNumber,
        abhaAddress: profile?.abhaAddress || prev.abhaAddress
      }));
      
      if (profile?.profilePhoto || profile?.photo) {
        const base64Data = profile.profilePhoto || profile.photo;
        const base64String = base64Data.startsWith('data:') ? base64Data : `data:image/jpeg;base64,${base64Data}`;
        setAbhaPhotoData(base64String);
        setAvatar(base64String);
      }
      
      setIsAbhaVerified(true);
      localStorage.removeItem('abhaVerifiedProfile');
    } catch (error) {
      localStorage.removeItem('abhaVerifiedProfile');
    }
  }, [customer, formik]);

  useEffect(() => {
    if (formik.values.gender && !abhaPhotoData) {
      const newAvatar = getRandomAvatar(formik.values.gender);
      setFieldValue('avatar', newAvatar); 
      setAvatar(getImageUrl(`avatar-${newAvatar}.png`, ImagePath.USERS)); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.gender, abhaPhotoData]);

  useEffect(() => {
    const filterRoles = formik?.values.roles?.filter((role) => role !== "patient");
    if (filterRoles.length >= 1) {
      setSelectedType("employee");
    } else {
      setSelectedType("patient");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.roles.length]);

  useEffect(() => {
    if (formik.values.careOfName) {
      setSearchQuery(formik.values.careOfName);
    }
  }, [formik.values.careOfName]);

  /**
   * Registering a patient whose ABHA is already in HMS: link it to that
   * patient (with the ABHA details or keeping the HMS ones) instead of
   * creating a duplicate, then hand that patient back like a new registration.
   */
  const linkAbhaToExistingPatient = async (profile, patient, useAbhaDetails) => {
    try {
      await syncPatientWithAbha(profile, { patientId: patient.id, useAbhaDetails });
      if (localStorage.getItem('comingFromAppointmentForm') === 'true') {
        const name = useAbhaDetails
          ? [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(' ')
          : patient.full_name;
        localStorage.setItem(
          'newlyRegisteredPatient',
          JSON.stringify({
            id: patient.id,
            full_name: name || patient.full_name,
            phone_number: String((useAbhaDetails ? profile.mobile : patient.phone_number) || '')
          })
        );
        localStorage.removeItem('comingFromAppointmentForm');
      }
      openSnackbar({
        open: true,
        message: useAbhaDetails
          ? `Patient already registered (UHID ${patient.id}). Details updated from ABHA.`
          : `Patient already registered (UHID ${patient.id}). ABHA linked; HMS details kept.`,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'success' }
      });
      handleFormSubmit?.();
      closeModal?.();
    } catch (error) {
      openSnackbar({
        open: true,
        message: extractAbhaErrorMessage(error, 'Failed to link the ABHA to the existing patient'),
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'error' }
      });
    }
  };

  /**
   * "Go with HIMS" on a new registration: link the ABHA to the existing
   * patient (keeping the HMS details) and continue as an edit of that patient.
   */
  const openExistingPatientForEdit = async (profile, patient, session) => {
    try {
      await syncPatientWithAbha(profile, { patientId: patient.id, useAbhaDetails: false });
      let full = patient;
      try {
        const found = await fetchUserDetails(patient.id);
        if (found?.id === patient.id) full = found;
      } catch (error) {
        console.error('Could not load the existing patient; using the lookup result:', error);
      }
      const linked = {
        ...full,
        abha_number: normalizeAbhaNumber(profile.abhaNumber) || full.abha_number,
        abha_address: full.abha_address || profile.abhaAddress
      };
      setSwitchedPatient(linked);
      formik.resetForm({ values: getInitialValues(linked) });
      setIsAbhaVerified(true);
      startAbhaSession(session, linked.phone_number);
      openSnackbar({
        open: true,
        message: `Patient already registered (UHID ${patient.id}). ABHA linked - review the details and save.`,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'info' }
      });
    } catch (error) {
      openSnackbar({
        open: true,
        message: extractAbhaErrorMessage(error, 'Failed to link the ABHA to the existing patient'),
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'error' }
      });
    }
  };

  /** Opens the "patient already registered" dialog when the ABHA number is known. Throws if the lookup fails. */
  const openExistingAbhaPatient = async (patientData) => {
    const match = await findPatientByAbhaNumber(patientData.abha_number);
    if (!match?.exists || !match.patient) return false;
    setExistingAbhaMatch({
      patient: match.patient,
      abhaAddress: patientData.abha_address,
      abhaNumber: patientData.abha_number
    });
    return true;
  };

  const handleExistingAbhaPatientConfirm = async ({ addAddress, makePreferred }) => {
    if (!existingAbhaMatch) return;
    const { patient, abhaAddress, abhaNumber } = existingAbhaMatch;
    setIsLinkingExistingPatient(true);
    try {
      if (addAddress) {
        await addPatientAbhaAddress({ patientId: patient.id, abhaAddress, abhaNumber, makePreferred });
      }
      if (localStorage.getItem('comingFromAppointmentForm') === 'true') {
        localStorage.setItem(
          'newlyRegisteredPatient',
          JSON.stringify({ id: patient.id, full_name: patient.full_name, phone_number: patient.phone_number || '' })
        );
        localStorage.removeItem('comingFromAppointmentForm');
      }
      openSnackbar({
        open: true,
        message: addAddress
          ? `Patient already registered (UHID ${patient.id}). ABHA address ${abhaAddress} added.`
          : `Patient already registered (UHID ${patient.id}).`,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'success' }
      });
      setExistingAbhaMatch(null);
      handleFormSubmit?.();
      closeModal?.();
    } catch (error) {
      openSnackbar({
        open: true,
        message: extractAbhaErrorMessage(error, 'Failed to add ABHA address to the existing patient'),
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setIsLinkingExistingPatient(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );

  // ABHA number / address come from ABDM once verified and must not be hand-edited.
  const isAbhaIdentityLocked = isAbhaVerified && !customer && Boolean(formik.values.abhaNumber || formik.values.abhaAddress);

  const createTitle = selectedType === 'patient' ? 'New Patient' : 'New User';
  const editTitle = selectedType === 'patient' ? 'Edit Customer' : 'Edit User';

  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {customer ? editTitle : createTitle}
              {!customer && selectedType === 'patient' && (
                <Button variant="contained" color="primary" onClick={() => setOpenAbhaModal(true)}>
                  Auto-fill via ABHA
                </Button>
              )}
            </DialogTitle>
            <Divider />
            {showDoctorProfileTab && (
              <Tabs
                value={editTab}
                onChange={(_, value) => setEditTab(value)}
                aria-label="Doctor user edit tabs"
                sx={{ px: 2.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="User Details" />
                <Tab label="Doctor Profile" />
              </Tabs>
            )}
            <DialogContent sx={{ p: 2.5, display: showDoctorProfileTab && editTab !== 0 ? 'none' : 'block' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                    <Avatar alt="Avatar 1" src={avatar} sx={{ width: 72, height: 72, border: '1px dashed' }} />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-fullName">Full Name <span style={{ color: 'red' }}>*</span></InputLabel>
                        <TextField
                          fullWidth
                          id="customer-fullName"
                          placeholder="Enter Full Name"
                          {...getFieldProps('fullName')}
                          error={Boolean(touched.fullName && errors.fullName)}
                          helperText={touched.fullName && errors.fullName}
                          disabled={isAbhaVerified}
                        />
                      </Stack>
                    </Grid>

                    {!skipRoleSelection && mode !== "add-patient" && <Grid item xs={12} sm={12}>
                      <Stack spacing={1}>
                        <FormControl fullWidth>
                          <InputLabel id="demo-multiple-checkbox-label">Select Role</InputLabel>
                          <Select
                            labelId="demo-multiple-checkbox-label"
                            id="demo-multiple-checkbox"
                            multiple
                            value={formik.values.roles || []}
                            onChange={(e) => {
                              const roleValues = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                              setFieldValue('roles', roleValues);
                              setSelectedRoles(roleValues);
                            }}
                            input={<OutlinedInput placeholder="Tag" />}
                            renderValue={(selected) =>
                              selected
                                .map((code) => allRoles.find((role) => role.code === code)?.name)
                                .filter(Boolean)
                                .join(', ')
                            }
                          >
                            {roleGroups.flatMap((group) => [
                              <ListSubheader
                                key={`${group.category}-header`}
                                disableSticky
                                sx={{ fontWeight: 700, color: 'primary.main', bgcolor: 'background.paper', lineHeight: 2.5 }}
                              >
                                {group.category}
                              </ListSubheader>,
                              ...group.roles.map((role) => (
                                <MenuItem key={role.code} value={role.code} sx={{ pl: 3 }}>
                                  <Checkbox checked={formik.values?.roles?.includes(role.code)} />
                                  <ListItemText primary={role.name} />
                                </MenuItem>
                              ))
                            ])}
                          </Select>
                        </FormControl>
                      </Stack>
                    </Grid>}
                    {!showDoctorProfileTab && !hideSpeciality && selectedType === 'employee' && (formik.values.roles.includes('doctor') &&
                      <>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            freeSolo
                            options={allSpecializations.map((s) => s.name)}
                            value={formik.values.speciality}
                            onChange={(_, newVal) => {
                              // when the user picks from the list
                              formik.setFieldValue('speciality', newVal || '');
                            }}
                            onInputChange={(_, newInput) => {
                              // when the user types
                              formik.setFieldValue('speciality', newInput);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select or type Speciality"
                                error={Boolean(touched.speciality && errors.speciality)}
                                helperText={touched.speciality && errors.speciality}
                              />
                            )}
                          />
                        </Grid>
                        
                        {(formik.values.roles.includes("doctor") || formik.values.roles.includes("superuser")) && (
                          <Grid item xs={12} sm={6}>
                            <Stack spacing={1}>
                              <TextField
                                fullWidth
                                id="doctor-qualification"
                                placeholder="Enter qualification (e.g., MBBS, MD)"
                                {...getFieldProps('qualification')}
                                error={Boolean(touched.qualification && errors.qualification)}
                                helperText={touched.qualification && errors.qualification}
                              />
                            </Stack>
                          </Grid>
                        )}
                      </>
                    )}
                    {canManageDoctorBooking && !isExistingDoctor && selectedType === 'employee' && formik.values.roles.includes('doctor') && (
                      <DoctorBookingFields
                        booking={formik.values.doctorBooking}
                        errors={formik.errors.doctorBooking}
                        setFieldValue={setFieldValue}
                        onPhotoChange={(file) => {
                          setDoctorProfilePhoto(file);
                          if (file) setFieldValue('doctorBooking.profile_photo', URL.createObjectURL(file));
                        }}
                      />
                    )}
                    <Grid item xs={15} sm={7}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-contact">Mobile number <span style={{ color: 'red' }}>*</span></InputLabel>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                          <Select name="countryCode" value={formik.values.countryCode}>
                            <MenuItem value="+91">+91</MenuItem>
                          </Select>
                          <Stack spacing={0} sx={{ flexGrow: 1 }}>
                            <TextField
                              fullWidth
                              id="customer-contact"
                              placeholder="Enter mobile"
                              {...getFieldProps('contact')}
                              error={Boolean(touched.contact && (errors?.contact || submitErrors?.phone_number))}
                              helperText={touched.contact && (errors?.contact || submitErrors?.phone_number)}
                              onInput={(e) => {
                                setSubmitErrors((state) => {
                                  delete state.phone_number;
                                  return state;
                                });
                                e.target.value = e.target.value.replace(/\D/g, '');
                              }}
                            />
                          </Stack>
                        </Stack>
                        {abhaMobileUpdatePending(abhaSession, formik.values.contact) &&
                          (abhaSession.isPhrLogin ? (
                            <Typography variant="caption" color="warning.main">
                              {PHR_MOBILE_UPDATE_MESSAGE}
                            </Typography>
                          ) : (
                            <AbhaUpdateMobile
                              title="Verify the new mobile number on the ABHA"
                              xToken={abhaSession.xToken}
                              abhaNumber={abhaSession.abhaNumber}
                              currentMobile={abhaSession.mobile}
                              fixedMobile={formik.values.contact}
                              onUpdated={(mobile) => setAbhaSession((prev) => (prev ? { ...prev, mobile } : prev))}
                            />
                          ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={5}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-age">Date of Birth <span style={{ color: 'red' }}>*</span></InputLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                          <DatePicker
                            disabled={isAbhaVerified}
                            value={formik.values.dob ? dayjs(formik.values.dob, "YYYY-MM-DD", true) : null}
                            onChange={(newValue) => setFieldValue("dob", newValue)}
                            onAccept={handleDateAccept}
                            maxDate={dayjs()}
                            slotProps={{
                              textField: {
                                disabled: isAbhaVerified,
                                error: Boolean(touched.dob && errors.dob),
                                helperText: <FormHelperText sx={{ color: 'error.main' }}>
                                  {touched.dob && errors.dob}
                                </FormHelperText>
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="flex-end">
                          <Box sx={{ width: '30%' }}>
                            <InputLabel htmlFor="careof-suffix-relation" sx={{ mb: '7px' }}>Care Of Information</InputLabel>
                            <Autocomplete
                              id="careof-suffix"
                              options={CAREOF_SUFFIXES}
                              getOptionLabel={(option) => option.label}
                              value={CAREOF_SUFFIXES.find(suffix => suffix.value === formik.values.careOfSuffix) || CAREOF_SUFFIXES[0]}
                              onChange={(event, newValue) => {
                                setFieldValue('careOfSuffix', newValue?.value || 'C/O');
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Relation"
                                  error={Boolean(touched.careOfSuffix && errors.careOfSuffix)}
                                />
                              )}
                            />
                          </Box>
                          <Box sx={{ width: '70%' }}>
                            <PatientSearchField
                              value={searchQuery}
                              onChange={(value) => {
                                setSearchQuery(value);
                                setFieldValue('careOfName', value);
                              }}
                              error={Boolean(touched.careOfName && errors.careOfName)}
                              helperText={touched.careOfName && errors.careOfName}
                              onPatientSelect={(patient) => {
                                setSearchQuery(patient.full_name);
                                setFieldValue('careOf', patient.id);
                                setFieldValue('careOfName', patient.full_name);
                              }}
                              field='Care Of Name'
                              allowCustomEntry={true}
                            />
                          </Box>
                        </Stack>
                        {(touched.careOfSuffix && errors.careOfSuffix) && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                            {errors.careOfSuffix}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-email">
                          Email Address {selectedType === "employee" && <span style={{ color: 'red' }}>*</span>}
                        </InputLabel>
                        <TextField
                          fullWidth
                          id="customer-email"
                          type="email"
                          placeholder="Enter your email"
                          {...formik.getFieldProps('email')}
                          error={Boolean(formik.touched.email && formik.errors.email)}
                          helperText={formik.touched.email && formik.errors.email}
                        />
                      </Stack>
                    </Grid>

                    {!skipPasswordFields && selectedType === "employee" &&
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <InputLabel htmlFor="customer-password">
                            {customer ? 'New password' : 'Create password'} {!customer && <span style={{ color: 'red' }}>*</span>}
                          </InputLabel>
                          <TextField
                            fullWidth
                            id="customer-password"
                            type="password"
                            placeholder="Enter the password"
                            {...formik.getFieldProps('password')}
                            error={Boolean(formik.touched.password && formik.errors.password)}
                            helperText={
                              formik.touched.password && formik.errors.password
                                ? formik.errors.password
                                : customer ? 'Leave blank to keep the current password' : ''
                            }
                          />
                        </Stack>
                      </Grid>
                    }
                    {!skipPasswordFields && selectedType === "employee" &&
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <InputLabel htmlFor="customer-confirmPassword">
                            {customer ? 'Confirm new password' : 'Confirm password'} {!customer && <span style={{ color: 'red' }}>*</span>}
                          </InputLabel>
                          <TextField
                            fullWidth
                            id="customer-confirmPassword"
                            type="password"
                            placeholder="Enter the password again"
                            {...formik.getFieldProps('confirmPassword')}
                            error={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                          />
                        </Stack>
                      </Grid>
                    }
                    
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-gender">Gender <span style={{ color: 'red' }}>*</span></InputLabel>
                        <RadioGroup row aria-label="payment-card" {...getFieldProps('gender')}>
                          <FormControlLabel disabled={isAbhaVerified} control={<Radio value={Gender.MALE} />} label={Gender.MALE} />
                          <FormControlLabel disabled={isAbhaVerified} control={<Radio value={Gender.FEMALE} />} label={Gender.FEMALE} />
                          <FormControlLabel disabled={isAbhaVerified} control={<Radio value="Other" />} label="Other" />
                        </RadioGroup>
                      </Stack>
                    </Grid> 

                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-maritalStatus">Marital Status</InputLabel>
                        <RadioGroup
                          row
                          name="maritalStatus"
                          value={formik.values.maritalStatus}
                          onChange={formik.handleChange}
                        >
                          <FormControlLabel value={MaritalStatus.MARRIED} control={<Radio />} label={MaritalStatus.MARRIED} />
                          <FormControlLabel value={MaritalStatus.UNMARRIED} control={<Radio />} label={MaritalStatus.UNMARRIED} />
                        </RadioGroup>
                        <FormHelperText sx={{ color: 'error.main' }}>
                          {formik.touched.maritalStatus && formik.errors.maritalStatus}
                        </FormHelperText>
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-bloodGroup">Blood Group</InputLabel>
                        <Select
                          fullWidth
                          id="customer-bloodGroup"
                          name="bloodGroup"
                          value={formik.values.bloodGroup}
                          onChange={formik.handleChange}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select Blood Group</em>
                          </MenuItem>
                          <MenuItem value="A+">A+</MenuItem>
                          <MenuItem value="A-">A-</MenuItem>
                          <MenuItem value="B+">B+</MenuItem>
                          <MenuItem value="B-">B-</MenuItem>
                          <MenuItem value="AB+">AB+</MenuItem>
                          <MenuItem value="AB-">AB-</MenuItem>
                          <MenuItem value="O+">O+</MenuItem>
                          <MenuItem value="O-">O-</MenuItem>
                        </Select>
                      </Stack>
                    </Grid>


                    {/* Aadhaar is collected only for staff accounts; patients are identified via ABHA (ABDM M1). */}
                    {selectedType === "employee" && (
                    <Grid item xs={15}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-aadhar">Aadhar Number {selectedType === "employee" && <span style={{ color: 'red' }}>*</span>}</InputLabel>
                        <TextField
                          fullWidth
                          id="customer-aadhar"
                          placeholder="Enter Aadhar"
                          {...getFieldProps('aadhar')}
                          error={Boolean(touched.aadhar && (errors.aadhar || submitErrors?.aadhar_card))}
                          helperText={touched.aadhar && (errors.aadhar || submitErrors?.aadhar_card)}
                          onInput={(e) => {
                            setSubmitErrors((state) => {
                              delete state?.aadhar_card;
                              return state;
                            });
                            e.target.value = e.target.value.replace(/\D/g, '');
                          }}
                        />
                      </Stack>
                    </Grid>
                    )}
                    <Grid item xs={8}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-abhaAddress">ABHA Address <span style={{ color: 'red' }}>*</span></InputLabel>
                        <TextField
                          fullWidth
                          id="customer-abhaAddress"
                          placeholder="e.g. patient@sbx"
                          {...getFieldProps('abhaAddress')}
                          InputProps={{ readOnly: isAbhaIdentityLocked }}
                          error={Boolean(touched.abhaAddress && (errors.abhaAddress || submitErrors?.abha_address))}
                          helperText={
                            (touched.abhaAddress && (errors.abhaAddress || submitErrors?.abha_address)) ||
                            'Needed to link records with ABDM. Leave blank if the patient has no ABHA.'
                          }
                          onChange={(e) => {
                            setSubmitErrors((state) => {
                              delete state?.abha_address;
                              return state;
                            });
                            formik.setFieldValue('abhaAddress', e.target.value.trim().toLowerCase());
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={7}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-abhaNumber">ABHA Number <span style={{ color: 'red' }}>*</span></InputLabel>
                        <TextField
                          fullWidth
                          id="customer-abhaNumber"
                          placeholder="14-digit ABHA Number"
                          inputProps={{ maxLength: 17, readOnly: isAbhaIdentityLocked }}
                          {...getFieldProps('abhaNumber')}
                          error={Boolean(touched.abhaNumber && (errors.abhaNumber || submitErrors?.abha_number))}
                          helperText={
                            (touched.abhaNumber && (errors.abhaNumber || submitErrors?.abha_number)) ||
                            '14 digits, hyphens allowed. Leave blank if the patient has no ABHA.'
                          }
                          onInput={(e) => {
                            setSubmitErrors((state) => {
                              delete state?.abha_number;
                              return state;
                            });
                            e.target.value = e.target.value.replace(/[^\d-]/g, '');
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-address">Address <span style={{ color: 'red' }}>*</span></InputLabel>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          id="customer-address"
                          placeholder="Enter Address / Aadhaar Address"
                          {...getFieldProps('address')}
                          error={Boolean(touched.address && errors.address)}
                          helperText={touched.address && errors.address}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={15}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-PIN">
                          State{selectedType !== 'employee' && <span style={{ color: 'red' }}> *</span>}
                          {' '}(district and P.O. help find the pincode)
                        </InputLabel>
                        <PincodeSelector
                          formik={formik}
                          setSelectedPincode={(value) => setFieldValue('pincode', value)}
                          state={formik.values.state}
                          onStateChange={(value) => setFieldValue('state', value)}
                          stateError={touched.state && errors.state}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={5}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-village">Village</InputLabel>
                        <TextField
                          fullWidth
                          id="customer-village"
                          placeholder="Enter Village (Optional)"
                          {...getFieldProps('village')}
                          error={Boolean(touched.village && errors.village)}
                          helperText={touched.village && errors.village}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={3}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-PIN">Pincode <span style={{ color: 'red' }}>*</span></InputLabel>
                        <TextField
                          fullWidth
                          id="customer-Pincode"
                          placeholder="Enter Pincode"
                          {...getFieldProps('pincode')}
                          error={Boolean(touched.pincode && errors.pincode)}
                          helperText={touched.pincode && errors.pincode}
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, '');
                          }}
                        />
                      </Stack>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="customer-panel">Panel</InputLabel>
                        <RadioGroup
                          aria-label="panel"
                          {...getFieldProps('tribe')}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            handlePanelChange(e);
                            formik.handleChange(e);
                            
                            let tribeNameValue = '';
                            
                            if (SPECIAL_PANEL_TYPES.includes(selectedValue)) {
                              tribeNameValue = selectedValue;
                            }
                            
                            formik.setFieldValue('tribeName', tribeNameValue);
                          }}
                        >
                          <Grid container spacing={0.5}>
                            <Grid item xs={3}>
                              <FormControlLabel 
                                control={<Radio size="small" value="Tribe" />} 
                                label="Tribe" 
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <FormControlLabel 
                                control={<Radio size="small" value="No Tribe" />} 
                                label="Non Tribe" 
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <FormControlLabel 
                                control={<Radio size="small" value="SD" />} 
                                label="Staff Dependent" 
                                sx={{ whiteSpace: 'nowrap', mb: 1 }}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <FormControlLabel 
                                control={<Radio size="small" value="SK" />} 
                                label="Sanskar Kendra" 
                                sx={{ whiteSpace: 'nowrap', ml: 2 }}
                              />
                            </Grid>

                            <Grid item xs={3}>
                              <FormControlLabel 
                                control={<Radio size="small" value="UJVNL" />} 
                                label="UJVNL" 
                              />
                            </Grid>
                            <Grid item xs={2.8}>
                              <FormControlLabel 
                                control={<Radio size="small" value="STAFF" />} 
                                label="Staff" 
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <FormControlLabel 
                                  control={<Radio size="small" value="CODE" />} 
                                  label="Others" 
                                />
                                <TextField 
                                  size="small"
                                  placeholder="Enter Code"
                                  sx={{ maxWidth: '300px' }}
                                  disabled={selectedPanel !== "CODE"}
                                  onChange={(e) => {
                                    if (selectedPanel === "CODE") {
                                      formik.setFieldValue('tribeName', e.target.value);
                                    }
                                  }}
                                  value={selectedPanel === "CODE" ? formik.values.tribeName : ""}
                                />
                              </Stack>
                            </Grid>
                          </Grid>
                        </RadioGroup>
                        <FormHelperText sx={{
                          color: 'error.main',
                          display: (touched.tribe && errors.tribe) ? 'block' : 'none'
                        }}>
                          {errors.tribe}
                        </FormHelperText>
                      </Stack>
                    </Grid>
                    {selectedPanel === "Tribe" ? (
                      <Grid item xs={15}>
                        <Stack spacing={1}>
                          <InputLabel htmlFor="customer-gender">Tribe Name<span style={{ color: 'red' }}>*</span></InputLabel>
                          <RadioGroup row aria-label="payment-card" {...getFieldProps('tribeName')}>
                            <FormControlLabel control={<Radio value="Jaunsari" />} label="Jaunsari" />
                            <FormControlLabel control={<Radio value="Boxa" />} label="Boxa" />
                            <FormControlLabel control={<Radio value="Other" />} label="Other" />
                          </RadioGroup>
                          <FormHelperText sx={{
                            color: 'error.main'
                          }}>
                            {touched.tribeName && errors.tribeName}
                          </FormHelperText>
                        </Stack>
                      </Grid>
                    ) : selectedPanel === "No Tribe" ? (
                      <Grid item xs={15}>
                        <Stack spacing={1}>
                          <InputLabel htmlFor="customer-gender">Religion<span style={{ color: 'red' }}>*</span></InputLabel>
                          <RadioGroup row aria-label="payment-card" {...getFieldProps('tribeName')}>
                            <FormControlLabel control={<Radio value="Hindu" />} label="Hindu" />
                            <FormControlLabel control={<Radio value="Muslim" />} label="Muslim" />
                            <FormControlLabel control={<Radio value="Christian" />} label="Christian" />
                            <FormControlLabel control={<Radio value="Others" />} label="Others" />
                          </RadioGroup>
                          <FormHelperText sx={{
                            color: 'error.main'
                          }}>
                            {(touched.tribeName && errors.tribeName) ? ERROR_NO_RELIGION_SELECTED : null}
                          </FormHelperText>
                        </Stack>
                      </Grid>
                    ) : null}
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            {showDoctorProfileTab && (
              <DialogContent sx={{ p: 2.5, display: editTab === 1 ? 'block' : 'none' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={allSpecializations.map((specialization) => specialization.name)}
                      value={formik.values.speciality}
                      onChange={(_, value) => setFieldValue('speciality', value || '')}
                      onInputChange={(_, value) => setFieldValue('speciality', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Speciality"
                          error={Boolean(touched.speciality && errors.speciality)}
                          helperText={touched.speciality && errors.speciality}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Qualification"
                      placeholder="e.g., MBBS, MD"
                      {...getFieldProps('qualification')}
                      error={Boolean(touched.qualification && errors.qualification)}
                      helperText={touched.qualification && errors.qualification}
                    />
                  </Grid>
                  {bookingProfileStatus === 'loaded' ? (
                    <DoctorBookingFields
                      booking={formik.values.doctorBooking}
                      errors={formik.errors.doctorBooking}
                      setFieldValue={setFieldValue}
                      onPhotoChange={(file) => {
                        setDoctorProfilePhoto(file);
                        if (file) setFieldValue('doctorBooking.profile_photo', URL.createObjectURL(file));
                      }}
                    />
                  ) : (
                    <Grid item xs={12}>
                      {bookingProfileStatus === 'failed' ? (
                        <Alert severity="error">
                          Online booking settings could not be loaded, so they won&apos;t be changed. Reopen this user to try again.
                        </Alert>
                      ) : (
                        <Stack direction="row" justifyContent="center" sx={{ py: 3 }}>
                          <CircularWithPath />
                        </Stack>
                      )}
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
            )}
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  {customer && (
                    <Tooltip title="Delete Customer" placement="top">
                      <IconButton onClick={() => setOpenAlert(true)} size="large" disabled={true} color="error">
                        <DeleteFilled />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
                <Grid item>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button color="error" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={isSubmitting} ref={submitButtonRef}>
                      {customer ? 'Edit' : 'Add'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
      {customer && <AlertCustomerDelete id={customer.id} title={customer.name} open={openAlert} handleClose={handleAlertClose} />}
      <AbhaVerification 
        open={openAbhaModal} 
        handleClose={() => setOpenAbhaModal(false)} 
        onProfileVerified={handleAbhaProfileVerified}
        openStepper={setOpenStepperModal}
        currentPatient={customer?.id ? customer : null}
      />
      <PatientStepper 
        open={openStepperModal}
        handleClose={() => setOpenStepperModal(false)}
        onAbhaResolved={handleAbhaProfileVerified}
      />
      <ExistingAbhaPatientDialog
        open={Boolean(existingAbhaMatch)}
        patient={existingAbhaMatch?.patient}
        newAbhaAddress={existingAbhaMatch?.abhaAddress || ''}
        onCancel={() => setExistingAbhaMatch(null)}
        onConfirm={handleExistingAbhaPatientConfirm}
        submitting={isLinkingExistingPatient}
      />
    </>
  );
}

FormCustomerAdd.propTypes = {
  customer: PropTypes.any,
  closeModal: PropTypes.func,
  type: PropTypes.oneOf(['patient', 'employee']),
  mode: PropTypes.string,
  /** ABHA verification session (from the appointment form's "Go with HIMS"). */
  abhaSession: PropTypes.object,
  handleFormSubmit: PropTypes.func,
  skipRoleSelection: PropTypes.bool,
  skipPasswordFields: PropTypes.bool,
  hideSpeciality: PropTypes.bool
};
